/**
 * Completion page - Final step of onboarding
 */

import {useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {Button} from '../../components/ui/Button';
import {Card} from '../../components/ui/Card';
import {Container} from '../../components/ui/Container';
import {ProgressBar} from '../../components/onboarding/ProgressBar';
import {useOnboardingStore} from '@voxapilot/shared/stores';
import {useAuthStore} from '@voxapilot/shared/stores';
import {ONBOARDING_STEPS} from '@voxapilot/shared/types';
import {ROUTES} from '../../config/constants';
import {api} from '../../config/firebase';

export function CompletionPage() {
  const navigate = useNavigate();
  const {completeStep, isCompleted} = useOnboardingStore();
  const {profile, setProfile} = useAuthStore();

  useEffect(() => {
    // Mark onboarding as completed
    const markComplete = async () => {
      try {
        const response = await api.auth.updateProfile({
          onboardingCompleted: true,
          onboardingStep: ONBOARDING_STEPS.COMPLETION,
          onboardingData: {
            ...profile?.onboardingData,
          },
        });
        if (response.success && response.data) {
          setProfile(response.data);
        }
        completeStep(ONBOARDING_STEPS.COMPLETION);
      } catch (error) {
        console.error('Failed to mark onboarding complete:', error);
        // Still mark as complete locally
        completeStep(ONBOARDING_STEPS.COMPLETION);
      }
    };

    if (!isCompleted) {
      markComplete();
    }
  }, [isCompleted, completeStep, setProfile]);

  const handleGoToDashboard = () => {
    navigate(ROUTES.APP);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 flex flex-col">
      <Container className="flex-1 flex flex-col items-center justify-center py-12">
        <div className="max-w-2xl w-full space-y-8">
          {/* Progress */}
          <div className="mb-8">
            <ProgressBar />
          </div>

          {/* Content */}
          <div className="text-center space-y-6">
            <div className="w-20 h-20 bg-gray-900 rounded-full mx-auto flex items-center justify-center">
              <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <h1 className="text-4xl sm:text-5xl font-heading font-bold text-textPrimary dark:text-gray-100">
              You're All Set!
            </h1>

            <p className="text-xl text-textSecondary dark:text-gray-400 max-w-xl mx-auto">
              Welcome to VoxaPilot! You're ready to start transforming your meetings with 
              intelligent transcription and AI-powered insights.
            </p>

            {/* Quick links */}
            <div className="grid md:grid-cols-3 gap-4 mt-12">
              <Card hover className="p-6">
                <div className="space-y-2">
                  <div className="w-10 h-10 bg-gray-100 dark:bg-gray-800 rounded-lg mx-auto flex items-center justify-center">
                    <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">M</span>
                  </div>
                  <h3 className="font-semibold text-textPrimary dark:text-gray-100">View Meetings</h3>
                  <p className="text-sm text-textSecondary dark:text-gray-400">See all your meetings</p>
                </div>
              </Card>
              <Card hover className="p-6">
                <div className="space-y-2">
                  <div className="w-10 h-10 bg-gray-100 dark:bg-gray-800 rounded-lg mx-auto flex items-center justify-center">
                    <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">P</span>
                  </div>
                  <h3 className="font-semibold text-textPrimary dark:text-gray-100">Edit Profile</h3>
                  <p className="text-sm text-textSecondary dark:text-gray-400">Update your information</p>
                </div>
              </Card>
              <Card hover className="p-6">
                <div className="space-y-2">
                  <div className="w-10 h-10 bg-gray-100 dark:bg-gray-800 rounded-lg mx-auto flex items-center justify-center">
                    <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">S</span>
                  </div>
                  <h3 className="font-semibold text-textPrimary dark:text-gray-100">Settings</h3>
                  <p className="text-sm text-textSecondary dark:text-gray-400">Configure preferences</p>
                </div>
              </Card>
            </div>

            {/* Action */}
            <div className="pt-8">
              <Button variant="primary" size="lg" onClick={handleGoToDashboard}>
                Go to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
}

